import '@/assets/main.css'

import { createApp, watchEffect } from 'vue'
import * as Sentry from '@sentry/vue'
import App from './App.vue'
import { createI18n } from 'vue-i18n'
import { router } from './router'
import en from '@/translations/en.json'
import fr from '@/translations/fr.json'
import de from '@/translations/de.json'
import es from '@/translations/es.json'
import it from '@/translations/it.json'
import nl from '@/translations/nl.json'
import pt from '@/translations/pt.json'
import ru from '@/translations/ru.json'
import { useMergedTranslations } from '@/composables/translations/useMergedTranslations.ts'
import { createToastPlugin } from '@ai/ui-components/toast'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { queryClient } from '@/lib/queryClient'
import { useFeatureFlags } from '@/composables/env/useFeatureFlags'
import { config } from '@/config'
import { initAnalytics, useAnalytics } from '@/composables/analytics/useAnalytics'
import { useMixpanel } from '@/composables/analytics/useMixpanel'
import { useAuthBff } from '@/composables/bff/useAuthBff'
import { useConfigurator } from '@/composables/configurator/useConfigurator'
import { vMpNoTrack } from '@/directives/vMpNoTrack'
import { createToastViewportStorePlugin } from '@/composables/toast/useToastViewportStore'

const { mergedMessages } = useMergedTranslations({ en, fr, de, es, it, nl, pt, ru })
const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  messages: mergedMessages.value,
})

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const app = createApp(App)

// Initialize Sentry in production and staging
const { isPreProduction } = useFeatureFlags()
if (isPreProduction) {
  Sentry.init({
    app,
    dsn: 'https://59d666563905d0755da56bea4b05e253@sentry.corp.dblc.io/310',
    environment: config.env || 'production',
    release: `virtual-agent-frontend@${__APP_VERSION__}`,
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.linkedErrorsIntegration(),
      Sentry.browserTracingIntegration(),
    ],
    ignoreErrors: [
      'No active session',
      'Session expired',
      'Request timeout',
      'ResizeObserver loop',
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% of transactions for performance monitoring
  })
}

// Initialize Mixpanel in sandbox
const { init: initMixpanel, ...mixpanelProvider } = useMixpanel()
initMixpanel()

initAnalytics(mixpanelProvider, () => {
  const { organization } = useAuthBff()
  const { configurationId } = useConfigurator()
  return {
    companyId: organization.value?.id ?? '',
    configId: configurationId.value ?? '',
  }
})

const { identify } = useAnalytics()
const { userId } = useAuthBff()
watchEffect(() => {
  if (userId.value) identify(userId.value)
})

app.directive('mp-no-track', vMpNoTrack)

app
  .use(VueQueryPlugin, { queryClient })
  .use(createToastPlugin())
  .use(createToastViewportStorePlugin())
  .use(i18n)
  .use(router)
  .mount('#app')
